/**
 * Cliente tipado del API de plataforma (`/api/v1`, docs/FASE-0.md §5), agrupado por recurso. Toda
 * llamada pasa por `authorizedRequest` de `./session.ts` (adjunta el Bearer vigente y reintenta
 * una vez ante `401`) y valida la respuesta con un esquema de `@continnua/contracts` antes de
 * devolverla — nunca se usa una respuesta sin validar (docs/FASE-1.md §6).
 *
 * `/health` y `/ready` son la excepción: viven en la raíz del IdP (`apps/idp/src/app.ts`), no bajo
 * `/api/v1`, y no requieren autenticación.
 */

import {
  type AuditLogEntry,
  AuditLogEntrySchema,
  type BillingCycleSummary,
  BillingCycleSummarySchema,
  type BillingProfile,
  BillingProfileSchema,
  type CreateCreditNoteInput,
  type CreateOAuthClientInput,
  type CreateOrganizationDomainInput,
  type CreateOrganizationInput,
  type CreatePlanInput,
  type CreateWebhookEndpointInput,
  type EntitlementsSnapshot,
  EntitlementsSnapshotSchema,
  type Invoice,
  type InvoiceDetail,
  InvoiceDetailSchema,
  InvoiceSchema,
  type InviteMemberInput,
  type ListOrganizationsQuery,
  type Membership,
  MembershipSchema,
  type Module,
  ModuleSchema,
  type OAuthClient,
  type OAuthClientCreated,
  OAuthClientCreatedSchema,
  OAuthClientSchema,
  type Operator,
  OperatorSchema,
  type OperatorTotpSetupResponse,
  OperatorTotpSetupResponseSchema,
  type OperatorTotpVerifyInput,
  type Organization,
  type OrganizationDomain,
  OrganizationDomainSchema,
  OrganizationSchema,
  PageSchema,
  type PaymentMethod,
  PaymentMethodSchema,
  type Plan,
  PlanSchema,
  type RegisterPaymentInput,
  type Subscription,
  SubscriptionSchema,
  type UpdateMemberInput,
  type UpdateOAuthClientInput,
  type UpdateOrganizationInput,
  type UpdatePlanInput,
  type UpsertBillingProfileInput,
  type UpsertModuleInput,
  type UpsertModuleResponse,
  UpsertModuleResponseSchema,
  type UpsertSubscriptionInput,
  type WebhookDelivery,
  WebhookDeliverySchema,
  type WebhookEndpoint,
  type WebhookEndpointCreated,
  WebhookEndpointCreatedSchema,
  WebhookEndpointSchema,
  type VoidInvoiceInput,
  type WebhookHealthResponse,
  WebhookHealthResponseSchema,
} from "@continnua/contracts";
import { z } from "zod";

import { toQueryString } from "../lib/query";
import { apiBaseUrl, ConsoleApiError, rawRequest } from "./http";
import { authorizedRequest } from "./session";

function itemsSchema<T extends z.ZodTypeAny>(item: T) {
  return z.object({ items: z.array(item) });
}

// --- Organizaciones ---------------------------------------------------------------------------

export type ListOrganizationsParams = Partial<Pick<ListOrganizationsQuery, "q" | "status" | "moduleCode" | "cursor" | "limit">>;

export const organizationsApi = {
  list: (params: ListOrganizationsParams = {}) =>
    authorizedRequest(`/api/v1/orgs${toQueryString(params)}`, { method: "GET" }, PageSchema(OrganizationSchema)),
  get: (orgId: string) => authorizedRequest<Organization>(`/api/v1/orgs/${orgId}`, { method: "GET" }, OrganizationSchema),
  create: (input: CreateOrganizationInput) =>
    authorizedRequest<Organization>("/api/v1/orgs", { method: "POST", body: input }, OrganizationSchema),
  update: (orgId: string, input: UpdateOrganizationInput) =>
    authorizedRequest<Organization>(`/api/v1/orgs/${orgId}`, { method: "PATCH", body: input }, OrganizationSchema),
  suspend: (orgId: string, reason?: string) =>
    authorizedRequest<Organization>(
      `/api/v1/orgs/${orgId}/suspend`,
      { method: "POST", body: reason ? { reason } : {} },
      OrganizationSchema,
    ),
  reactivate: (orgId: string) =>
    authorizedRequest<Organization>(`/api/v1/orgs/${orgId}/reactivate`, { method: "POST" }, OrganizationSchema),
};

// --- Miembros ----------------------------------------------------------------------------------

export const membersApi = {
  list: (orgId: string) =>
    authorizedRequest<{ items: Membership[] }>(`/api/v1/orgs/${orgId}/members`, { method: "GET" }, itemsSchema(MembershipSchema)),
  invite: (orgId: string, input: InviteMemberInput) =>
    authorizedRequest<Membership>(`/api/v1/orgs/${orgId}/members`, { method: "POST", body: input }, MembershipSchema),
  update: (orgId: string, userId: string, input: UpdateMemberInput) =>
    authorizedRequest<Membership>(`/api/v1/orgs/${orgId}/members/${userId}`, { method: "PATCH", body: input }, MembershipSchema),
  remove: (orgId: string, userId: string) => authorizedRequest(`/api/v1/orgs/${orgId}/members/${userId}`, { method: "DELETE" }, z.void()),
};

// --- Dominios propios ---------------------------------------------------------------------------

export const domainsApi = {
  list: (orgId: string) =>
    authorizedRequest<{ items: OrganizationDomain[] }>(
      `/api/v1/orgs/${orgId}/domains`,
      { method: "GET" },
      itemsSchema(OrganizationDomainSchema),
    ),
  create: (orgId: string, input: CreateOrganizationDomainInput) =>
    authorizedRequest<OrganizationDomain>(`/api/v1/orgs/${orgId}/domains`, { method: "POST", body: input }, OrganizationDomainSchema),
  verify: (orgId: string, domainId: string) =>
    authorizedRequest<OrganizationDomain>(
      `/api/v1/orgs/${orgId}/domains/${domainId}/verify`,
      { method: "POST" },
      OrganizationDomainSchema,
    ),
};

// --- Módulos (manifiesto en solo lectura desde la consola, docs/FASE-1.md §6) -------------------

export const modulesApi = {
  list: () => authorizedRequest<{ items: Module[] }>("/api/v1/modules", { method: "GET" }, itemsSchema(ModuleSchema)),
  get: (code: string) => authorizedRequest<Module>(`/api/v1/modules/${code}`, { method: "GET" }, ModuleSchema),
  /** Expuesto por completitud del contrato; la consola no ofrece un editor de manifiesto (§6, pantalla 3). */
  upsert: (code: string, manifest: UpsertModuleInput) =>
    authorizedRequest<UpsertModuleResponse>(`/api/v1/modules/${code}`, { method: "PUT", body: manifest }, UpsertModuleResponseSchema),
};

// --- Clientes OAuth de un módulo -----------------------------------------------------------------

export const clientsApi = {
  list: (moduleCode: string) =>
    authorizedRequest<{ items: OAuthClient[] }>(`/api/v1/modules/${moduleCode}/clients`, { method: "GET" }, itemsSchema(OAuthClientSchema)),
  create: (moduleCode: string, input: CreateOAuthClientInput) =>
    authorizedRequest<OAuthClientCreated>(`/api/v1/modules/${moduleCode}/clients`, { method: "POST", body: input }, OAuthClientCreatedSchema),
  rotateSecret: (moduleCode: string, clientId: string) =>
    authorizedRequest<OAuthClientCreated>(
      `/api/v1/modules/${moduleCode}/clients/${clientId}/rotate-secret`,
      { method: "POST" },
      OAuthClientCreatedSchema,
    ),
  remove: (moduleCode: string, clientId: string) =>
    authorizedRequest(`/api/v1/modules/${moduleCode}/clients/${clientId}`, { method: "DELETE" }, z.void()),
  update: (moduleCode: string, clientId: string, input: UpdateOAuthClientInput) =>
    authorizedRequest<OAuthClient>(
      `/api/v1/modules/${moduleCode}/clients/${clientId}`,
      { method: "PATCH", body: input },
      OAuthClientSchema,
    ),
};

// --- Planes por módulo ---------------------------------------------------------------------------

export const plansApi = {
  list: (moduleCode: string) =>
    authorizedRequest<{ items: Plan[] }>(`/api/v1/modules/${moduleCode}/plans`, { method: "GET" }, itemsSchema(PlanSchema)),
  create: (moduleCode: string, input: CreatePlanInput) =>
    authorizedRequest<Plan>(`/api/v1/modules/${moduleCode}/plans`, { method: "POST", body: input }, PlanSchema),
  update: (moduleCode: string, planId: string, input: UpdatePlanInput) =>
    authorizedRequest<Plan>(`/api/v1/modules/${moduleCode}/plans/${planId}`, { method: "PATCH", body: input }, PlanSchema),
};

// --- Suscripciones (desde el detalle de organización) -------------------------------------------

export const subscriptionsApi = {
  list: (orgId: string) =>
    authorizedRequest<{ items: Subscription[] }>(`/api/v1/orgs/${orgId}/subscriptions`, { method: "GET" }, itemsSchema(SubscriptionSchema)),
  upsert: (orgId: string, moduleCode: string, input: UpsertSubscriptionInput) =>
    authorizedRequest<Subscription>(`/api/v1/orgs/${orgId}/subscriptions/${moduleCode}`, { method: "PUT", body: input }, SubscriptionSchema),
  cancel: (orgId: string, moduleCode: string) =>
    authorizedRequest<Subscription>(`/api/v1/orgs/${orgId}/subscriptions/${moduleCode}`, { method: "DELETE" }, SubscriptionSchema),
};

export const entitlementsApi = {
  get: (orgId: string, moduleCode: string) =>
    authorizedRequest<EntitlementsSnapshot>(
      `/api/v1/orgs/${orgId}/modules/${moduleCode}/entitlements`,
      { method: "GET" },
      EntitlementsSnapshotSchema,
    ),
};

// --- Webhooks de un módulo -------------------------------------------------------------------

export const webhooksApi = {
  list: (moduleCode: string) =>
    authorizedRequest<{ items: WebhookEndpoint[] }>(
      `/api/v1/modules/${moduleCode}/webhooks`,
      { method: "GET" },
      itemsSchema(WebhookEndpointSchema),
    ),
  create: (moduleCode: string, input: CreateWebhookEndpointInput) =>
    authorizedRequest<WebhookEndpointCreated>(
      `/api/v1/modules/${moduleCode}/webhooks`,
      { method: "POST", body: input },
      WebhookEndpointCreatedSchema,
    ),
  rotateSecret: (moduleCode: string, endpointId: string) =>
    authorizedRequest<WebhookEndpointCreated>(
      `/api/v1/modules/${moduleCode}/webhooks/${endpointId}/rotate-secret`,
      { method: "POST" },
      WebhookEndpointCreatedSchema,
    ),
  remove: (moduleCode: string, endpointId: string) =>
    authorizedRequest(`/api/v1/modules/${moduleCode}/webhooks/${endpointId}`, { method: "DELETE" }, z.void()),
  test: (moduleCode: string, endpointId: string) =>
    authorizedRequest<WebhookDelivery>(`/api/v1/modules/${moduleCode}/webhooks/${endpointId}/test`, { method: "POST" }, WebhookDeliverySchema),
};

// --- Auditoría y salud del repartidor de webhooks (docs/FASE-1.md §6, pantallas 6 y 7) ---------

/**
 * `Partial<{...}>` (no una `interface` con propiedades opcionales) a propósito: `toQueryString`
 * exige `Record<string, QueryValue>`, y solo un tipo mapeado homomórfico como `Partial<T>` es
 * estructuralmente asignable a un tipo con índice de firma `string` sin listar cada propiedad
 * explícitamente (una `interface` corriente no lo es, así falle `tsc` con "Index signature for
 * type 'string' is missing").
 */
export type ListAuditParams = Partial<{
  actorType: "user" | "operator" | "module" | "system";
  orgId: string;
  action: string;
  targetType: string;
  cursor: string;
  limit: number;
}>;

export type { AuditLogEntry };

export const auditApi = {
  /** `GET /api/v1/audit`: del evento más reciente al más antiguo, solo para operadores. */
  list: (params: ListAuditParams = {}) =>
    authorizedRequest(`/api/v1/audit${toQueryString(params)}`, { method: "GET" }, PageSchema(AuditLogEntrySchema)),
};

export const webhookHealthApi = {
  /** `GET /api/v1/webhooks/health`: entregas pendientes/fallidas y endpoints por módulo. */
  get: () =>
    authorizedRequest<WebhookHealthResponse>("/api/v1/webhooks/health", { method: "GET" }, WebhookHealthResponseSchema),
};

// --- Operador autenticado ------------------------------------------------------------------------

export const operatorsApi = {
  me: () => authorizedRequest<Operator>("/api/v1/operators/me", { method: "GET" }, OperatorSchema),
  /** Inicia el alta del segundo factor: devuelve el `otpauth://` para el autenticador. */
  totpSetup: () =>
    authorizedRequest<OperatorTotpSetupResponse>(
      "/api/v1/operators/totp/setup",
      { method: "POST" },
      OperatorTotpSetupResponseSchema,
    ),
  /** Confirma el segundo factor con el primer código de seis dígitos. */
  totpVerify: (code: string) =>
    authorizedRequest<Operator>(
      "/api/v1/operators/totp/verify",
      { method: "POST", body: { code } satisfies OperatorTotpVerifyInput },
      OperatorSchema,
    ),
};


// --- Facturación (docs/FASE-6.md §7) ------------------------------------------------------------

/**
 * Las acciones de escritura exigen un operador `owner` o `billing`; con `support` el API responde
 * `403`. La pantalla las oculta además (`apps/console/src/pages/Facturacion.tsx`), pero **la
 * autoridad es el API**: ocultar un botón no es un control de acceso.
 */
export const billingApi = {
  listInvoices: (orgId: string, params: { cursor?: string; limit?: number } = {}) =>
    authorizedRequest(`/api/v1/orgs/${orgId}/invoices${toQueryString(params)}`, { method: "GET" }, PageSchema(InvoiceSchema)),
  getInvoice: (invoiceId: string) =>
    authorizedRequest<InvoiceDetail>(`/api/v1/invoices/${invoiceId}`, { method: "GET" }, InvoiceDetailSchema),
  registerPayment: (invoiceId: string, input: RegisterPaymentInput) =>
    authorizedRequest(
      `/api/v1/invoices/${invoiceId}/payments`,
      { method: "POST", body: input },
      z.object({ invoice: InvoiceSchema, cycle: BillingCycleSummarySchema }).passthrough(),
    ),
  voidInvoice: (invoiceId: string, input: VoidInvoiceInput) =>
    authorizedRequest<Invoice>(`/api/v1/invoices/${invoiceId}/void`, { method: "POST", body: input }, InvoiceSchema),
  createCreditNote: (orgId: string, input: CreateCreditNoteInput) =>
    authorizedRequest<Invoice>(`/api/v1/orgs/${orgId}/credit-notes`, { method: "POST", body: input }, InvoiceSchema),
  getProfile: (orgId: string) =>
    authorizedRequest<BillingProfile>(`/api/v1/orgs/${orgId}/billing-profile`, { method: "GET" }, BillingProfileSchema),
  saveProfile: (orgId: string, input: UpsertBillingProfileInput) =>
    authorizedRequest<BillingProfile>(`/api/v1/orgs/${orgId}/billing-profile`, { method: "PUT", body: input }, BillingProfileSchema),
  listPaymentMethods: (orgId: string) =>
    authorizedRequest(`/api/v1/orgs/${orgId}/payment-methods`, { method: "GET" }, itemsSchema(PaymentMethodSchema)),
  runCycle: (orgId?: string) =>
    authorizedRequest<BillingCycleSummary>(
      "/api/v1/billing/cycle",
      { method: "POST", body: orgId === undefined ? {} : { orgId } },
      BillingCycleSummarySchema,
    ),
};

// --- Salud (raíz del IdP, sin autenticación, docs/FASE-0.md §5 "Salud") --------------------------

const HealthStatusSchema = z.object({ status: z.literal("ok") });
export type HealthStatus = z.infer<typeof HealthStatusSchema>;

const ReadyStatusSchema = z.object({ status: z.enum(["ok", "unavailable"]) });
export type ReadyStatus = z.infer<typeof ReadyStatusSchema>;

export const healthApi = {
  health: () => rawRequest<HealthStatus>("/health", { method: "GET" }, HealthStatusSchema),
  /**
   * `GET /ready` responde `503` con `{ status: "unavailable" }` cuando la base de datos no
   * responde: es un estado válido del dominio, no un error de transporte, así que se evalúa fuera
   * de `rawRequest` (que trata cualquier `!res.ok` como fallo) para poder mostrarlo en la pantalla
   * de Salud en vez de una alerta genérica.
   */
  ready: async (): Promise<ReadyStatus> => {
    try {
      const res = await fetch(`${apiBaseUrl()}/ready`);
      const body: unknown = await res.json().catch(() => undefined);
      const parsed = ReadyStatusSchema.safeParse(body);
      if (parsed.success) return parsed.data;
      return { status: "unavailable" };
    } catch {
      return { status: "unavailable" };
    }
  },
};

export { ConsoleApiError };
